/**
 * Delivered / "Seen by", without the N+1.
 *
 * The icon is decided by `userReads` — the reader list the message item already carries
 * — so rendering a page of bubbles costs zero extra requests. `readAt` only exists on
 * `GET /:cid/messages/:messageId/reads`, and only the open hover card needs it, so that
 * query is gated on the card's open state.
 *
 * This replaced a GraphQL-era `ReadIndicator` that took `UserReadServiceMessage` rows and
 * fetched eagerly per bubble; that component is gone, so this is the only read indicator
 * in the app. Keep the lazy `isOpen` gate below — dropping it restores the N+1.
 *
 * A message the automated assistant posted shows NO indicator at all — neither state says
 * anything worth reading. "Delivered" and "Seen" answer "did my message reach the other
 * side", and nobody is waiting on the answer for a system announcement. The gate lives
 * here rather than at the call site so every surface that renders an indicator inherits
 * it; `authoredByBot` is resolved server-side, since which account is the bot is
 * environment-specific.
 */

import { cn } from "cn";
import { format } from "date-fns";
import { useState } from "react";
import Avatar from "@/components/ui/avatar/avatar";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { BwCheckmarkLine, BwSeenCheckmarkLine } from "@/components/ui/icons";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DISPLAY_DATETIME_FULL_NO_YEAR } from "@/libs/date-fns";
import type { ChannelMessage } from "../_api/channel-endpoints";
import { useChannelReadReceipts } from "../_api/use-channel-read-receipts";

type ChannelReadIndicatorProps = {
  cid: string;
  message: ChannelMessage;
  /**
   * Which edge of the checkmark the "Seen by" card lines up with — the caller's call,
   * because only the caller knows where the icon sits. The default suits an indicator on
   * the left (the header row of the classic bubble); a right-aligned one, like the
   * Conversation bubble's footer, passes `"end"` so the card opens inward instead of
   * running off the edge of the thread.
   */
  align?: "start" | "center" | "end";
  className?: string;
};

export function ChannelReadIndicator({
  cid,
  message,
  align = "start",
  className,
}: ChannelReadIndicatorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: receipts, isLoading } = useChannelReadReceipts(
    cid,
    message.id,
    isOpen,
  );

  if (message.authoredByBot) return null;

  const hasReaders = (message.userReads?.length ?? 0) > 0;

  if (!hasReaders) {
    return (
      <Tooltip>
        <TooltipTrigger className={className}>
          <BwCheckmarkLine
            className="text-text-dark-low"
            data-test="check-line-icon"
          />
        </TooltipTrigger>
        <TooltipContent side="bottom">Delivered</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <HoverCard open={isOpen} onOpenChange={setIsOpen}>
      <HoverCardTrigger className={className}>
        <BwSeenCheckmarkLine
          className="text-text-primary"
          data-test="check-double-line-icon"
        />
      </HoverCardTrigger>
      <HoverCardContent
        align={align}
        className={cn(
          /* sorted */
          "overflow-hidden",
          "w-60 max-h-80",
          "p-1",
          "bg-bg-surface-elevate-low border-border-dark-low border rounded-lg shadow-lg",
        )}
      >
        <div className="py-1.5 px-3 text-sm text-text-dark-low">Seen by</div>
        {isLoading && !receipts ? (
          <div className="flex items-center gap-2 px-3 py-1.5">
            <Skeleton className="size-8 rounded-full" />
            <Skeleton className="w-28 h-5 rounded" />
          </div>
        ) : (
          receipts?.map((receipt) => (
            <div key={receipt.id} className="py-1.5 px-3">
              <div className="flex items-center gap-2">
                <Avatar
                  src={receipt.user.avatarUrl ?? ""}
                  name={receipt.user.name ?? ""}
                />
                <div className="text-text-dark-high h-9">
                  <p className="text-sm text-text-dark-max line-clamp-1">
                    {receipt.user.firstName ?? receipt.user.name}
                  </p>
                  <span className="text-xs leading-4">
                    {format(receipt.readAt, DISPLAY_DATETIME_FULL_NO_YEAR)}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
